/**
 * Mirror of backend ROLE_PERMISSIONS from functions/src/auth/permissions.ts.
 * Must be kept in sync with the backend.
 */

export type OrgRole =
  | "org_admin"
  | "data_manager"
  | "cra"
  | "pi"
  | "site_coordinator"
  | "viewer";

export type Permission =
  | "platform:manage"
  | "connections:manage"
  | "connections:read"
  | "templates:manage"
  | "templates:read"
  | "events:read"
  | "events:write"
  | "events:delete"
  | "edc_data:read"
  | "edc_data:write"
  | "ai:use"
  | "messaging:send"
  | "branding:read"
  | "dashboard:read"
  | "dashboard:manage"
  | "audit:read"
  | "invites:manage"
  | "org:manage"
  | "billing:manage"
  | "agents:manage"
  | "branding:manage";

export const ROLE_PERMISSIONS: Record<OrgRole, Set<Permission>> = {
  org_admin: new Set([
    "connections:manage", "connections:read",
    "templates:manage", "templates:read",
    "events:read", "events:write", "events:delete",
    "edc_data:read", "edc_data:write",
    "ai:use", "messaging:send", "branding:read",
    "dashboard:read", "dashboard:manage",
    "audit:read",
    "invites:manage", "org:manage", "billing:manage",
    "agents:manage", "branding:manage",
  ]),
  data_manager: new Set([
    "connections:read",
    "templates:read",
    "events:read", "events:write",
    "edc_data:read", "edc_data:write",
    "ai:use", "messaging:send", "branding:read",
    "dashboard:read", "dashboard:manage",
    "audit:read",
    "invites:manage",
    "agents:manage",
  ]),
  cra: new Set([
    "templates:read",
    "events:read", "events:write",
    "edc_data:read",
    "ai:use", "messaging:send", "branding:read",
    "dashboard:read",
  ]),
  pi: new Set([
    "templates:read",
    "events:read", "events:write",
    "edc_data:read", "edc_data:write",
    "ai:use", "messaging:send", "branding:read",
    "dashboard:read",
  ]),
  site_coordinator: new Set([
    "templates:read",
    "events:read", "events:write",
    "edc_data:read",
    "ai:use", "messaging:send", "branding:read",
    "dashboard:read",
  ]),
  viewer: new Set([
    "templates:read",
    "events:read",
    "edc_data:read",
    "ai:use", "branding:read",
    "dashboard:read",
  ]),
};

export function hasPermission(role: OrgRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
}

export function hasAnyPermission(role: OrgRole, permissions: Permission[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}
